import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { compose, pure } from 'recompose';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Field, FormSpy } from 'react-final-form';

import { getExpensesCollectionRefs, getIsUserDealTeamOwner } from 'selectors';

import { Hr } from 'components/shared';
import { Condition, FormFlex, FormBox, FormGroupAdapter, AddressFormGroupAdapter, FormLegend, FormGeocoderProvider } from 'components/shared/form';

import {
  composeValidators,
  validateAccepted,
  validateEmail,
  validatePhone,
  validateRequired,
  validatePayPalRequired,
  getIn,
} from 'utils';
import AddressFields from './AddressFields';
import QuestionIcon from './QuestionMark';

const formBoxTwoColumns = [1, 1, 1 / 2];

const propTypes = {
  refs: PropTypes.shape({
    countriesCollection: PropTypes.arrayOf(PropTypes.shape({})),
    statesCollection: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  isUserDealTeamOwner: PropTypes.bool,
  isAllPropertiesMultiFamily: PropTypes.bool,
};
const defaultProps = {
  refs: {
    statesCollection: [],
    countriesCollection: [],
  },
  isUserDealTeamOwner: false,
  isAllPropertiesMultiFamily: false,
};

function Fields({ isUserDealTeamOwner, isAllPropertiesMultiFamily }) {
  return (
    <Fragment>
      <FormFlex>
        <FormBox width={1}>
          <StyledLegend>Personal Information</StyledLegend>
        </FormBox>
      </FormFlex>
      <FormFlex>
        <StyledFormBox pl={150} width={formBoxTwoColumns}>
          <Field
            name="appraisalPayment.firstName"
            label="First Name"
            required
            validate={validateRequired()}
            component={FormGroupAdapter}
          />
        </StyledFormBox>
        <StyledFormBox pr={150} width={formBoxTwoColumns}>
          <Field
            name="appraisalPayment.lastName"
            label="Last Name"
            required
            validate={validateRequired()}
            component={FormGroupAdapter}
          />
        </StyledFormBox>
      </FormFlex>

      <FormFlex>
        <StyledFormBox pl={150} width={formBoxTwoColumns}>
          <Field
            name="appraisalPayment.email"
            label="Email"
            required
            validate={composeValidators(validateRequired(), validateEmail())}
            component={FormGroupAdapter}
          />
        </StyledFormBox>
        <StyledFormBox pr={150} mb={40} width={formBoxTwoColumns}>
          <Field
            name="appraisalPayment.phone"
            label="Phone"
            required
            validate={composeValidators(validateRequired(), validatePhone())}
            typeOfMask="phone"
            component={FormGroupAdapter}
          />
        </StyledFormBox>
      </FormFlex>

      <FormFlex>
        <FormBox width={1}>
          <StyledLegend>Billing Information</StyledLegend>
        </FormBox>
      </FormFlex>

      <FormGeocoderProvider namespace='appraisalPayment'>
        <FormFlex>
          <StyledFormBox pl={150} pr={150} width={1}>
            <Field
              name="appraisalPayment.address"
              label="Street Address"
              required
              validate={validateRequired()}
              component={AddressFormGroupAdapter}
            />
          </StyledFormBox>
        </FormFlex>
        <AddressFields />
      </FormGeocoderProvider>

      <FormFlex>
        <FormBox width={1}>
          <StyledLegend>Payment Information</StyledLegend>
        </FormBox>
      </FormFlex>

      <FormFlex>
        <StyledFormBox pl={150} pr={150} width={1}>
          <Field
            name="appraisalPayment.creditCard.nameOnCard"
            label="Name on Card"
            required
            type='paypalNameField'
            validate={validatePayPalRequired()}
            component={FormGroupAdapter}
          />
        </StyledFormBox>
      </FormFlex>
      <FormFlex>
        <StyledFormBox pl={150} pr={150} width={1}>
          <Field
            name="appraisalPayment.creditCard.number"
            label="Card Number"
            required
            validate={validatePayPalRequired()}
            type='paypalNumberField'
            component={FormGroupAdapter}
          />
        </StyledFormBox>
      </FormFlex>
      <FormFlex>
        <StyledFormBox pl={150} width={formBoxTwoColumns}>
          <Field
            label="Expiration Date"
            name="appraisalPayment.creditCard.expiryDate"
            required
            validate={validatePayPalRequired()}
            component={FormGroupAdapter}
            type='paypalExpiryField'

          />
        </StyledFormBox>
        <StyledFormBox mb={40} pr={150} width={formBoxTwoColumns}>
          <Field
            name="appraisalPayment.creditCard.verificationValue"
            label="CVV"
            required
            validate={validatePayPalRequired()}
            component={FormGroupAdapter}
            type='paypalCVVField'
          />
        </StyledFormBox>
      </FormFlex>
      <Field
        name="appraisalPayment.rushAmount"
        render={({ input: { value } }) =>
          value && (
            <FormFlex>
              <FormBox width={1}>
                <CheckboxWithTooltipContainer>
                  <CheckboxWrapper>
                    <Field
                      name="appraisalPayment.rush"
                      type="checkbox"
                      label={`Valuation Rush ($${value})`}
                      inlineGroup
                      labelOrder={1}
                      errorOrder={2}
                      component={FormGroupAdapter}
                    />
                    <QuestionIcon />
                  </CheckboxWrapper>
                </CheckboxWithTooltipContainer>
              </FormBox>
            </FormFlex>

          )
        }
      />
      <FormFlex>
        <FormBox width={1}>
          <Field
            name="appraisalPayment.accessContactRequired"
            type="checkbox"
            label="If you prefer someone else to coordinate property access with the appraiser"
            inlineGroup
            labelOrder={1}
            errorOrder={2}
            component={FormGroupAdapter}
          />
        </FormBox>
      </FormFlex>

      <Condition when="appraisalPayment.accessContactRequired" is="true">
        <FormFlex>
          <FormBox width={1}>
            <StyledLegend>Access Contact Information</StyledLegend>
          </FormBox>
        </FormFlex>
        <FormFlex>
          <StyledFormBox pl={150} width={formBoxTwoColumns}>
            <Field
              component={FormGroupAdapter}
              label="First Name"
              name="appraisalPayment.accessContact.firstName"
              required
              validate={validateRequired()}
            />
          </StyledFormBox>
          <StyledFormBox pr={150} width={formBoxTwoColumns}>
            <Field
              component={FormGroupAdapter}
              label="Last Name"
              name="appraisalPayment.accessContact.lastName"
              required
              validate={validateRequired()}
            />
          </StyledFormBox>
        </FormFlex>

        <FormFlex>
          <StyledFormBox pl={150} width={formBoxTwoColumns}>
            <Field
              component={FormGroupAdapter}
              label="Email"
              name="appraisalPayment.accessContact.email"
              required
              validate={composeValidators(validateRequired(), validateEmail())}
            />
          </StyledFormBox>

          <StyledFormBox mb={40} pr={150} width={formBoxTwoColumns}>
            <Field
              component={FormGroupAdapter}
              label="Phone #"
              name="appraisalPayment.accessContact.phone"
              required
              typeOfMask="phone"
              validate={composeValidators(validateRequired(), validatePhone())}
            />
          </StyledFormBox>
        </FormFlex>
      </Condition>

      <FormSpy
        subscription={{ values: true }}
        render={({ values, form }) => {
          const accessContactRequired = getIn('appraisalPayment.accessContactRequired', values);
          const primaryContact = {
            firstName: getIn('appraisalPayment.firstName', values),
            lastName: getIn('appraisalPayment.lastName', values),
            email: getIn('appraisalPayment.email', values),
            phone: getIn('appraisalPayment.phone', values)
          };

          const currentAccessContact = {
            firstName: getIn('appraisalPayment.accessContact.firstName', values),
            lastName: getIn('appraisalPayment.accessContact.lastName', values),
            email: getIn('appraisalPayment.accessContact.email', values),
            phone: getIn('appraisalPayment.accessContact.phone', values)
          };

          React.useEffect(() => {
            if (!accessContactRequired) {
              const hasChanges = Object.keys(primaryContact).some(key =>
                currentAccessContact[key] !== primaryContact[key]
              );

              if (hasChanges) {
                Object.keys(primaryContact).forEach(key => {
                  form.change(`appraisalPayment.accessContact.${key}`, primaryContact[key] || '');
                });
              }
            } else {
              const hasContent = Object.values(currentAccessContact).some(value => value);
              if (hasContent) {
                Object.keys(currentAccessContact).forEach(key => {
                  form.change(`appraisalPayment.accessContact.${key}`, '');
                });
              }
            }
          }, [accessContactRequired, ...Object.values(primaryContact)]);

          return null;
        }}
      />

      <FormFlex>
        <FormBox width={1}>
          <Field
            name="appraisalPayment.creditCardAuthorizationTerms"
            type="checkbox"
            label="I agree to allow Crestar Funding, LLC to charge the card provided in this authorization form, as per the terms outlined above"
            inlineGroup
            labelOrder={1}
            errorOrder={2}
            required
            validate={validateAccepted()}
            component={FormGroupAdapter}
          />
        </FormBox>
      </FormFlex>

      <FormFlex>
        <FormBox width={1}>
          <Hr />
        </FormBox>
      </FormFlex>

      {
        isUserDealTeamOwner && !isAllPropertiesMultiFamily && (
          <FormSpy
            subscription={{ values: true }}
            render={({ values }) => {
              const notifyBorrowerRequired = getIn('appraisalPayment.notifyBorrowerRequired', values);
              if (notifyBorrowerRequired) {
                return (
                  <FormFlex>
                    <FormBox width={1}>
                      <Field
                        name="appraisalPayment.notifyBorrower"
                        type="checkbox"
                        label="Send Payment Confirmation Email to Borrower"
                        inlineGroup
                        labelOrder={1}
                        errorOrder={2}
                        component={FormGroupAdapter}
                      />
                    </FormBox>
                  </FormFlex>
                );
              }

              return null;
            }}
          />
        )
      }
    </Fragment >
  );
}

const StyledLegend = styled(FormLegend)`
  margin-bottom: 1rem;
  font-size: var(--font-size-m);
  color: var(--color-primary);
  font-weight: 600;
`;

const StyledFormBox = styled(FormBox)`
  @media (max-width: 990px) {
    padding-left: 10px !important;
    padding-right: 10px !important;
  }
`;

const CheckboxWithTooltipContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  width: 40%;
  @media (max-width: 768px) {
    width: 50%;
  }
  @media (max-width: 468px) {
    width: 100%;
  }
`;

const CheckboxWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: start;
`;

Fields.propTypes = propTypes;
Fields.defaultProps = defaultProps;

function mapStateToProps(state) {
  return {
    refs: getExpensesCollectionRefs(state),
    isUserDealTeamOwner: getIsUserDealTeamOwner(state),
  };
}

const enhance = compose(withRouter, connect(mapStateToProps), pure);

export default enhance(Fields);
